import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, ImageIcon, Plus, Monitor, Smartphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const EMPTY = { title: "", text: "", cta_label: "", cta_url: "", image_desktop: "", image_mobile: "", product_ids: [], active: true, order: 1, start_at: "", end_at: "" };

function BannerPreview({ b, mobile, testId }) {
  const img = mobile ? (b.image_mobile || b.image_desktop) : b.image_desktop;
  return (
    <div className={`relative overflow-hidden border border-hair bg-ink text-bone ${mobile ? "w-44" : "w-full"}`} data-testid={testId}>
      {img && <img src={absoluteMediaUrl(img)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />}
      <div className={`relative ${mobile ? "p-4" : "p-8"}`}>
        <div className={`font-serif ${mobile ? "text-lg" : "text-2xl"} leading-tight`}>{b.title || "Titolo banner"}</div>
        <p className={`${mobile ? "text-[10px]" : "text-xs"} mt-2 opacity-80 leading-relaxed`}>{b.text || "Testo del banner…"}</p>
        <span className={`inline-block mt-3 border border-bone/60 ${mobile ? "px-2 py-1 text-[8px]" : "px-4 py-2 text-[10px]"} tracking-[0.14em] uppercase`}>
          {b.cta_label || "Pulsante"}
        </span>
      </div>
    </div>
  );
}

function BannerForm({ value, onChange, onUpload, busy, products, testId }) {
  const deskRef = useRef(null);
  const mobRef = useRef(null);
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target ? e.target.value : e });
  const toggleProduct = (id) => onChange({
    ...value,
    product_ids: value.product_ids.includes(id) ? value.product_ids.filter((x) => x !== id) : [...value.product_ids, id],
  });
  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 border border-ink hover:bg-ink hover:text-bone transition-colors disabled:opacity-60";
  return (
    <div className="grid gap-5" data-testid={testId}>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Titolo</Label>
          <Input value={value.title} onChange={set("title")} className="rounded-none mt-1" data-testid={`${testId}-title`} />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo del pulsante</Label>
          <Input value={value.cta_label} onChange={set("cta_label")} className="rounded-none mt-1" data-testid={`${testId}-cta-label`} />
        </div>
      </div>
      <div>
        <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo</Label>
        <Textarea rows={2} value={value.text} onChange={set("text")} className="rounded-none mt-1" data-testid={`${testId}-text`} />
      </div>
      <div>
        <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Collegamento del pulsante</Label>
        <Input value={value.cta_url} onChange={set("cta_url")} className="rounded-none mt-1" placeholder="/shop oppure /shop?preventivo=1" data-testid={`${testId}-cta-url`} />
        <p className="text-[11px] text-taupe mt-1">Per il modulo prezzo riservato Shop usa: /shop?preventivo=1</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => deskRef.current?.click()} disabled={busy} className={btn} data-testid={`${testId}-upload-desktop`}>
          <Upload size={14} /> Immagine desktop
        </button>
        <button type="button" onClick={() => mobRef.current?.click()} disabled={busy} className={btn} data-testid={`${testId}-upload-mobile`}>
          <Upload size={14} /> Immagine mobile
        </button>
        <input ref={deskRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { onUpload("image_desktop", e.target.files?.[0]); e.target.value = ""; }} />
        <input ref={mobRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { onUpload("image_mobile", e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="border border-hair p-2">{value.image_desktop ? <img src={absoluteMediaUrl(value.image_desktop)} alt="desktop" className="h-20 w-full object-cover" /> : <div className="h-20 flex items-center justify-center text-taupe"><ImageIcon size={16} /></div>}</div>
        <div className="border border-hair p-2">{value.image_mobile ? <img src={absoluteMediaUrl(value.image_mobile)} alt="mobile" className="h-20 w-full object-cover" /> : <div className="h-20 flex items-center justify-center text-taupe text-[10px]">Come desktop</div>}</div>
      </div>
      <div>
        <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Prodotti / novità Shop da mostrare (opzionale)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 max-h-48 overflow-y-auto" data-testid={`${testId}-products`}>
          {products.map((p) => (
            <label key={p.id} className={`flex items-center gap-2 border p-2 cursor-pointer text-xs ${value.product_ids.includes(p.id) ? "border-ink bg-bone" : "border-hair"}`}>
              <input type="checkbox" checked={value.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} data-testid={`${testId}-prod-${p.id}`} />
              <span className="truncate">{p.name} · {eur(p.price)}</span>
            </label>
          ))}
          {products.length === 0 && <p className="text-xs text-taupe col-span-3">Nessun prodotto pubblicato.</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Ordine</Label>
          <Input type="number" min={1} value={value.order} onChange={(e) => onChange({ ...value, order: Number(e.target.value) })} className="rounded-none mt-1" data-testid={`${testId}-order`} />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Inizio (opz.)</Label>
          <Input type="datetime-local" value={value.start_at ? value.start_at.slice(0, 16) : ""} onChange={(e) => onChange({ ...value, start_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-none mt-1" data-testid={`${testId}-start`} />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Fine (opz.)</Label>
          <Input type="datetime-local" value={value.end_at ? value.end_at.slice(0, 16) : ""} onChange={(e) => onChange({ ...value, end_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-none mt-1" data-testid={`${testId}-end`} />
        </div>
        <label className="flex items-end gap-2 pb-2 cursor-pointer min-h-[44px]">
          <Switch checked={value.active} onCheckedChange={(v) => onChange({ ...value, active: v })} data-testid={`${testId}-active`} />
          <span className="text-xs">Attivo</span>
        </label>
      </div>
      <div>
        <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe flex items-center gap-2"><Monitor size={12} /> Anteprima desktop</Label>
        <div className="mt-2"><BannerPreview b={value} testId={`${testId}-preview-desktop`} /></div>
        <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe flex items-center gap-2 mt-4"><Smartphone size={12} /> Anteprima mobile</Label>
        <div className="mt-2"><BannerPreview b={value} mobile testId={`${testId}-preview-mobile`} /></div>
      </div>
    </div>
  );
}

export function BannersPanel() {
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newBanner, setNewBanner] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/banners");
      setBanners(data);
    } catch { toast.error("Errore caricamento banner"); }
  };
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => p.published))).catch(() => {});
  }, []);

  const uploadImage = async (field, file, apply) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      apply(data.url);
      toast.success("Immagine caricata");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento"); }
    finally { setBusy(false); }
  };

  const save = async (b) => {
    setBusy(true);
    try {
      await api.put(`/admin/banners/${b.id}`, b);
      toast.success("Banner salvato");
      load();
    } catch { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/banners", { ...newBanner, order: banners.length + 1 });
      toast.success("Banner creato");
      setAdding(false); setNewBanner(EMPTY); load();
    } catch { toast.error("Errore creazione"); }
    finally { setBusy(false); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="banners-panel">
      <p className="text-xs text-taupe leading-relaxed">
        I banner compaiono nella homepage come sezioni editoriali: non coprono la navigazione e non sono popup.
        Sono visibili solo se attivi e nel periodo impostato. Le immagini sono conservate nello storage del sito e incluse nei backup.
      </p>
      {banners.map((b, i) => (
        <BannerEditor key={b.id} banner={b} index={i} products={products} busy={busy} onSave={save} onUpload={uploadImage} btn={btn} onSaved={load} />
      ))}
      {adding ? (
        <div className="bg-white border border-hair p-5 md:p-8">
          <div className="eyebrow">Nuovo banner</div>
          <div className="mt-6">
            <BannerForm value={newBanner} onChange={setNewBanner} busy={busy} products={products}
              onUpload={(f, file) => uploadImage(f, file, (url) => setNewBanner((p) => ({ ...p, [f]: url })))} testId="banner-new" />
          </div>
          <div className="flex gap-2 mt-6">
            <button onClick={create} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="banner-create-save">Crea banner</button>
            <button onClick={() => setAdding(false)} className={`${btn} border border-ink`} data-testid="banner-create-cancel">Annulla</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="banner-add">
          <Plus size={14} /> Aggiungi banner
        </button>
      )}
    </div>
  );
}

function BannerEditor({ banner, index, products, busy, onSave, onUpload, btn, onSaved }) {
  const [v, setV] = useState(banner);
  useEffect(() => setV(banner), [banner]);
  const dirty = JSON.stringify(v) !== JSON.stringify(banner);
  return (
    <div className="bg-white border border-hair p-5 md:p-8" data-testid={`banner-editor-${banner.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="eyebrow">Banner {index + 1}{v.active ? "" : " · disattivato"}</div>
        <span className={`text-[10px] tracking-[0.14em] uppercase ${v.active ? "text-emerald-700" : "text-taupe"}`} data-testid={`banner-status-${banner.id}`}>
          {v.active ? "Attivo" : "Disattivato"}
        </span>
      </div>
      <div className="mt-6">
        <BannerForm value={v} onChange={setV} busy={busy} products={products}
          onUpload={(f, file) => onUpload(f, file, (url) => setV((p) => ({ ...p, [f]: url })))} testId={`banner-${banner.id}`} />
      </div>
      <div className="flex gap-2 mt-6">
        <button onClick={() => onSave(v)} disabled={busy || !dirty} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid={`banner-save-${banner.id}`}>Salva banner</button>
        {dirty && <button onClick={() => setV(banner)} className={`${btn} border border-ink`} data-testid={`banner-reset-${banner.id}`}>Annulla modifiche</button>}
        <button onClick={async () => {
          if (!window.confirm(`Eliminare il banner "${banner.title}"?\n\nNon sarà più mostrato sul sito. Puoi anche semplicemente spegnerlo con il toggle senza eliminarlo.`)) return;
          try { await api.delete(`/admin/banners/${banner.id}`); toast.success("Banner eliminato"); onSaved(); }
          catch { toast.error("Eliminazione non riuscita"); }
        }} className={`${btn} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`banner-delete-${banner.id}`}>Elimina</button>
      </div>
    </div>
  );
}
